import React, { ReactNode, useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import { DetailEpisode, SeriesApiResponse, SeriesDetails, SeriesEpisodesApiResponse, TVSeriesDetailEpisode } from 'types/Asset';
import { decodeId, isHuubOrKidsPath } from 'utils/fnUrl';
import { PageWrapper } from 'components/Pages/PageDetailCommon/PageDetailCommon.css';
import { PageDetailLoading } from 'components/Pages/PageDetailCommon/PageDetailLoading';
import { Navigation } from 'components/Pages/PageDetailCommon/PageDetailCommon';
import { PageErrorNotFound } from 'components/Pages/PageErrorNotFound/PageErrorNotFound';
import { FetchParamById, FetchResponse, FetchParamByIdAndChannel } from 'types/ApiTypes';
import { useDetailsReloadOnPlaybackUpdate } from 'providers/usePlayback/PlaybackContext';
import { NetworkErrorAlert, useGlobalNetworkError, useGlobalNoInternet, withNetworkCheck } from 'hooks/withNetworkCheck/withNetworkCheck';
import { getSeriesPageHelmetData } from 'utils/fnHelmet';
import { HelmetWrapper } from 'components/HelmetWrapper/HelmetWrapper';
import { QUERY_PARAM_EPISODE, QUERY_PARAM_EVENT } from 'types/SeriesDetails';
import { scrollToTop } from 'utils/fnScroll';
import { DetailPageScreenName } from 'types/Tracking';
import { useApp } from 'providers/useApp/AppContext';
import { detailEpisodeToListEpisode, seriesDetailsParser } from 'utils/fnParser';
import { isEpisodeTrailer } from 'utils/fnData';
import { getStartEndTimes, isNow, isPast } from 'utils/fnDate';
import { useRecording } from 'providers/useRecording/RecordingContext';
import { useAuth } from 'providers/useAuth/AuthContext';
import { savePageInfo } from 'utils/pageInfoStorage';
import SeriesDetailsEpisodeSelector from './components/SeriesDetailsEpisodeSelector';

type PageHeaderInput = {
    series: SeriesDetails<DetailEpisode | TVSeriesDetailEpisode>;
    imdbRating?: number;
    episode?: DetailEpisode | TVSeriesDetailEpisode;
    overlayActive: boolean;
    setOverlayActive: (value: boolean) => void;
    listingId?: string;
};

type PropTypes = {
    id: string;
    type: 'TV' | 'VOD';
    configId: string;
    screenName: DetailPageScreenName;
    executor: (param: FetchParamByIdAndChannel) => Promise<FetchResponse<SeriesDetails<DetailEpisode | TVSeriesDetailEpisode>>>;
    pageHeader: (input: PageHeaderInput) => ReactNode;
    fetchVodSeriesDetails: (vodSeriesId: string, callback: (data: any) => any) => Promise<any>;
    fetchVODAssetDetails: (vodAssetId: string, callback: (data: any) => any) => Promise<any>;
    fetchProgramDetails: (
        programSeriesId: string,
        parser: (data: any) => SeriesDetails<TVSeriesDetailEpisode>
    ) => Promise<FetchResponse<any>>;
    fetchEpisodes: (programSeriesId: string, parser: (data: any) => SeriesDetails<TVSeriesDetailEpisode>) => Promise<FetchResponse<any>>;
    nextEpisode: TVSeriesDetailEpisode;
    nextEpisodeReady: boolean;
    fetchListing: (programSeriesId: string, parser: (data: any) => SeriesDetails<DetailEpisode>) => Promise<FetchResponse<any>>;
};

const findCurrentOrLatestPastEpisode = (episodes: TVSeriesDetailEpisode[]): TVSeriesDetailEpisode | undefined => {
    const liveEpisode = episodes.find((ep) => {
        const listing = ep.listings?.[0];
        if (!listing?.start || !listing?.duration) return false;
        const { startTime, endTime } = getStartEndTimes(listing.start, listing.duration);
        return isNow(startTime, endTime);
    });

    if (liveEpisode) return liveEpisode;

    return episodes
        .filter((ep) => {
            const listing = ep.listings?.[0];
            if (!listing?.start || !listing?.duration) return false;
            const { endTime } = getStartEndTimes(listing.start, listing.duration);
            return isPast(endTime);
        })
        .sort((a, b) => b.listings[0].start - a.listings[0].start)[0];
};

const PageSeriesDetailWrapped: React.FC<PropTypes> = ({
    type,
    id,
    screenName,
    pageHeader,
    fetchVodSeriesDetails,
    fetchProgramDetails,
    fetchEpisodes,
    fetchListing,
    nextEpisode,
    nextEpisodeReady,
    fetchVODAssetDetails,
}) => {
    const { onNoInternet } = useGlobalNoInternet();
    const { onNetworkError } = useGlobalNetworkError();
    const { appLanguage } = useApp();
    const [selectedEpisode, setSelectedEpisode] = useState<string>(null);
    const [selectedSeason, setSelectedSeason] = useState<number>(null);
    const [initialLaneScrollEnabled, setInitialLaneScrollEnabled] = useState(false);
    const [resetEpisodeListScroll, setResetEpisodeListScroll] = useState(false);
    const { recordingsResponse, getRecordings, updateIsRecorded, recordingsReady } = useRecording();
    const { cacheKey } = useAuth();
    const navigate = useNavigate();
    const [trailerEpisodes, setTrailerEpisodes] = useState<(DetailEpisode | TVSeriesDetailEpisode)[]>([]);
    const [parsedSeriesDetails, setParsedSeriesDetails] = useState(null);
    const [parsedEpisodes, setParsedEpisodes] = useState([]);
    const [episode, setEpisode] = useState(null);
    const location = useLocation();
    const urlQueryParams = new URLSearchParams(location.search);
    const isEpisodeDetails = urlQueryParams.has(QUERY_PARAM_EVENT) || urlQueryParams.has(QUERY_PARAM_EPISODE);
    const isVOD = type !== 'TV';
    const [overlayActive, setOverlayActive] = useState(false);

    const useCreateFetcher = <T = any,>(fetchFunction: (id: string, callback: (data: any) => any) => Promise<any>) =>
        useDataFetcher<T, FetchParamById>((param) => fetchFunction(param.id, (data) => data), onNoInternet, onNetworkError);

    const { error: listingError, response: listingResponse, fetcher: listingFetcher } = useCreateFetcher(fetchListing);
    const {
        error: vodSeriesError,
        response: vodSeriesResponse,
        fetcher: vodSeriesFetcher,
    } = useCreateFetcher<SeriesApiResponse>(fetchVodSeriesDetails);
    const { error: vodAssetError, response: vodAssetResponse, fetcher: vodAssetFetcher } = useCreateFetcher(fetchVODAssetDetails);
    const {
        error: programDetailsError,
        response: programDetailsResponse,
        fetcher: programDetailsFetcher,
    } = useCreateFetcher<SeriesApiResponse>(fetchProgramDetails);
    const {
        error: seriesEpisodesError,
        response: seriesEpisodesResponse,
        fetcher: seriesEpisodesFetcher,
    } = useCreateFetcher<SeriesEpisodesApiResponse>(fetchEpisodes);

    const seriesData = useMemo(() => {
        if (isVOD ? !vodSeriesResponse : !programDetailsResponse || !seriesEpisodesResponse) {
            return null;
        }

        return seriesDetailsParser(isVOD ? vodSeriesResponse : programDetailsResponse, seriesEpisodesResponse, isVOD);
    }, [isVOD ? vodSeriesResponse : programDetailsResponse, seriesEpisodesResponse, type]);

    useDetailsReloadOnPlaybackUpdate(type === 'VOD' ? vodSeriesFetcher : programDetailsFetcher, id);

    useEffect(() => {
        if (seriesData) {
            const { parsedSeriesDetails: seriesDetails, parsedEpisodes: episodes } = seriesData;
            setParsedSeriesDetails(seriesDetails);
            setParsedEpisodes(episodes.filter((ep) => !isEpisodeTrailer(ep)));
            const filteredTrailerEpisodes = episodes
                .filter((ep) => isEpisodeTrailer(ep))
                .filter((ep, index, all) => all.findIndex((other) => other.id === ep.id) === index);
            setTrailerEpisodes(filteredTrailerEpisodes);
        }
    }, [seriesData]);

    useEffect(() => {
        if (selectedSeason !== 0 && selectedEpisode?.includes('preview')) {
            setSelectedEpisode(null);

            if (urlQueryParams.has(QUERY_PARAM_EPISODE)) {
                urlQueryParams.delete(QUERY_PARAM_EPISODE);
            }

            navigate(
                {
                    pathname: location.pathname,
                    search: urlQueryParams.toString(),
                },
                { replace: true }
            );
        }
    }, [selectedEpisode, selectedSeason]);

    savePageInfo('Series details');

    const selectedEventId = urlQueryParams.get(QUERY_PARAM_EVENT);
    const selectedEpisodeId = urlQueryParams.get(QUERY_PARAM_EPISODE);

    useEffect(() => {
        if (!id) return;
        const seriesId = decodeId(id);
        (type === 'VOD' ? vodSeriesFetcher : programDetailsFetcher)({ id: seriesId }, true);
        seriesEpisodesFetcher({ id: seriesId }, true);
    }, [id, cacheKey]);

    useEffect(() => {
        if (!id || !isEpisodeDetails) return;
        if (type === 'VOD') {
            vodAssetFetcher({ id: selectedEpisodeId }, true);
        } else {
            listingFetcher({ id: selectedEventId ?? decodeId(id) }, true);
        }
    }, [id, isEpisodeDetails, selectedEventId, selectedEpisodeId]);

    useEffect(() => {
        getRecordings();
    }, []);

    useEffect(() => {
        const selectEpisode = () => {
            if (isEpisodeDetails) {
                const detail = type === 'VOD' ? vodAssetResponse : listingResponse;
                const ep = detailEpisodeToListEpisode(detail, type === 'VOD');
                if (ep) {
                    setEpisode(ep);
                }
            } else if (parsedEpisodes?.length > 0) {
                const episodeToSet =
                    type === 'TV' ? findCurrentOrLatestPastEpisode(parsedEpisodes) || nextEpisode : nextEpisode || parsedEpisodes[0];
                setEpisode(episodeToSet);
            } else if (trailerEpisodes.length > 0) {
                setEpisode(trailerEpisodes[0]);
            }
        };

        selectEpisode();
    }, [location.search, parsedEpisodes, trailerEpisodes, isEpisodeDetails, listingResponse, vodAssetResponse, type, nextEpisode]);

    useEffect(() => {
        if (!recordingsResponse?.results || !episode) return;

        const channelId = (episode as TVSeriesDetailEpisode).listings?.[0]?.channelId;
        const programSeriesId = (episode as TVSeriesDetailEpisode).program_series_id;
        const listingId = (episode as TVSeriesDetailEpisode).listings?.[0]?.id;

        const existingRecording =
            channelId && programSeriesId
                ? recordingsResponse.results.some(
                      (recording) =>
                          recording.listing.channel_id === channelId && recording.listing.program.program_series_id === programSeriesId
                  )
                : listingId && recordingsResponse.results.some((recording) => recording.listing.id === listingId);

        updateIsRecorded(!!existingRecording);
    }, [recordingsResponse, episode]);

    useEffect(() => {
        setInitialLaneScrollEnabled(!!seriesData);
    }, [seriesData]);

    useEffect(() => {
        setResetEpisodeListScroll(false);
    }, [resetEpisodeListScroll]);

    const listingId: string | undefined = (episode as any)?.listings?.[0]?.id || (episode as any)?.id;

    const selectedEpisodeReady = !isEpisodeDetails || !!episode;
    const ready = parsedSeriesDetails && nextEpisodeReady && (isVOD || recordingsReady) && selectedEpisodeReady;

    const detailError = programDetailsError || seriesEpisodesError || listingError || vodAssetError || vodSeriesError;
    if (detailError) {
        if (detailError?.response?.status === 404) {
            return <PageErrorNotFound />;
        }
        return <NetworkErrorAlert networkError={detailError?.response?.status || -1} onRetryClicked={() => window.location.reload()} />;
    }

    const onEpisodeSelected = (episodeId: string) => {
        if (episodeId === selectedEpisode) {
            return;
        }

        scrollToTop();

        urlQueryParams.set(QUERY_PARAM_EPISODE, episodeId);

        if (urlQueryParams.has(QUERY_PARAM_EVENT)) {
            urlQueryParams.delete(QUERY_PARAM_EVENT);
        } else {
            urlQueryParams.delete(QUERY_PARAM_EPISODE);
        }

        navigate({
            pathname: location.pathname,
            search: urlQueryParams.toString(),
        });
    };

    const onSeasonSelected = (season: number) => {
        if (season === selectedSeason) {
            return;
        }

        setSelectedSeason(season);
        setResetEpisodeListScroll(true);
    };

    return (
        <PageWrapper>
            {!ready && <PageDetailLoading isSeries={true} />}

            {ready && (
                <HelmetWrapper {...getSeriesPageHelmetData(parsedSeriesDetails, appLanguage, type)}>
                    <Navigation overlayActive={overlayActive} />
                    {pageHeader({
                        series: parsedSeriesDetails,
                        episode,
                        overlayActive,
                        setOverlayActive,
                        listingId,
                    })}
                </HelmetWrapper>
            )}

            {ready && parsedEpisodes && episode && parsedSeriesDetails && (
                <SeriesDetailsEpisodeSelector
                    type={type}
                    screenName={screenName}
                    episodeList={parsedEpisodes}
                    selected={selectedSeason ?? (parsedEpisodes.length > 0 ? episode.season_number : 0)}
                    onSeasonSelected={onSeasonSelected}
                    episode={episode}
                    onEpisodeSelected={onEpisodeSelected}
                    initialLaneScrollEnabled={initialLaneScrollEnabled}
                    resetEpisodeListScroll={resetEpisodeListScroll}
                    trailerEpisodes={trailerEpisodes}
                    hideChannelLogo
                    hideGenre={isHuubOrKidsPath(location.pathname)}
                />
            )}
        </PageWrapper>
    );
};

export const PageSeriesDetail = withNetworkCheck(PageSeriesDetailWrapped);
